// Central content store for the site — sourced from public profiles
// (GitHub, YouTube, LinkedIn) for Gaurav Bhadauria / Engineer Codewala.

export const profile = {
  name: "Gaurav Bhadauria",
  handle: "@EngineerCodewala",
  roles: [
    "Founder, Engineer Codewala",
    "Ex Software Engineer",
    "YouTuber",
    "React Native Developer",
  ],
  location: "Noida, India",
  tagline: "I build products, ship apps, and've taught React Native to 25K+ learners over 6+ years.",
  bio: [
    "I'm Gaurav Bhadauria, founder of Engineer Codewala — a developer, tech mentor, and content creator from India. I started out as a software engineer, then went all-in on building things of my own. 6+ years in, I'm still shipping.",
    "I lead a small, focused team of full-stack developers who deliver complete digital products end to end — mobile apps, web platforms, scalable backends, and custom admin panels. We've shipped 20+ products across India, UAE, and international markets, working with startups, SMEs, and individual founders.",
    "Beyond building, I mentor developers through 1-on-1 sessions and my YouTube channel, where I've taught React Native to 25K+ learners — 18K+ subscribers, 3.3M+ views, and 180K+ hours watched.",
  ],
  email: "gauravofficial1995@gmail.com",
  whatsapp: "https://wa.me/+918383006681",
  socials: {
    youtube: "https://www.youtube.com/@EngineerCodewala",
    linkedin: "https://linkedin.com/in/gauravbhadauria1995",
    github: "https://github.com/Gauravbhadauria",
    instagram: "https://www.instagram.com/engineercodewala/",
    website: "https://www.engineercodewala.in",
  },
};

export const stats = [
  { value: 20, suffix: "+", label: "Products Shipped" },
  { value: 18, suffix: "K+", label: "YouTube Subscribers" },
  { value: 3.3, suffix: "M+", label: "Total Views" },
  { value: 180, suffix: "K+", label: "Watch Hours" },
  { value: 25, suffix: "K+", label: "Learners Taught" },
  { value: 6, suffix: "+", label: "Years Experience" },
];

export const services = [
  {
    title: "Mobile Apps",
    icon: "phone",
    description:
      "Cross-platform & native apps, store deployment, animations, push notifications, maps, and real-time features.",
    stack: ["React Native", "Flutter", "Android", "iOS"],
  },
  {
    title: "Web & Frontend",
    icon: "code",
    description:
      "Responsive websites, SPAs, portals, and dashboards — SEO-ready and performance-first.",
    stack: ["React.js", "Next.js", "TypeScript", "Tailwind CSS"],
  },
  {
    title: "Backend & APIs",
    icon: "server",
    description:
      "Scalable server architecture, REST & GraphQL APIs, authentication, DB design, cloud deployment.",
    stack: ["Node.js", "Express", "MongoDB", "Firebase"],
  },
  {
    title: "Admin Panels",
    icon: "dashboard",
    description:
      "Custom dashboards with role-based access, analytics, and real-time data management.",
    stack: ["React Admin", "Chart.js", "Socket.io"],
  },
  {
    title: "Tech Mentorship",
    icon: "graduation",
    description:
      "1-on-1 mentorship — real project code reviews, system design, and a freelancing roadmap.",
    stack: ["React Native", "System Design", "Freelancing"],
  },
  {
    title: "YouTube Education",
    icon: "youtube",
    description:
      "Weekly tutorials on Engineer Codewala — React Native, full-stack dev, and career growth.",
    stack: ["Tutorials", "Career Tips", "Live Coding"],
  },
];

export const techStack = {
  Mobile: ["React Native", "Flutter", "Android", "iOS", "Kotlin"],
  "Frontend & Web": ["React.js", "Next.js", "TypeScript", "JavaScript", "Redux", "Tailwind CSS"],
  "Backend & Cloud": ["Node.js", "Express.js", "MongoDB", "Firebase", "GraphQL", "Socket.io"],
  "Tools & DevOps": ["Git", "GitHub", "Figma", "Jira", "Postman"],
};

export const projects = [
  {
    title: "AI-Powered Classified Platform",
    region: "UAE",
    description:
      "A smart classified ads marketplace powered by AI — auto-categorisation, intelligent price suggestions, and personalised recommendations. Delivered end-to-end: mobile app, web portal, admin panel.",
    scope: ["Mobile App", "Web Portal", "Backend", "Admin Panel"],
    stack: ["React Native", "Next.js", "Node.js", "MongoDB", "AI/ML"],
  },
  {
    title: "Music Streaming App",
    region: "Global",
    description:
      "A full-featured music app with artist profiles, album management, playlist creation, offline playback, and social sharing, built for iOS and Android.",
    scope: ["Mobile App (iOS + Android)", "Backend", "Streaming Infra"],
    stack: ["React Native", "Node.js", "Firebase", "Push Notifications"],
  },
  {
    title: "Job Portal",
    region: "UAE",
    description:
      "A comprehensive job platform connecting employers and candidates — smart filters, resume upload, recruiter dashboard, application tracking, automated alerts.",
    scope: ["Mobile App", "Web Portal", "Backend", "Admin Panel"],
    stack: ["React Native", "React.js", "Node.js", "MongoDB", "Firebase Auth"],
  },
  {
    title: "EV Bike Booking App",
    region: "India",
    description:
      "Electric bike booking platform for urban commuters — locate, book, unlock via QR, live tracking, in-app payments, with a fleet management panel for operators.",
    scope: ["Mobile App", "Backend", "Admin Panel"],
    stack: ["React Native", "Live GPS", "Node.js", "Payment Gateway", "Socket.io"],
  },
  {
    title: "Trailer Launch & Rating App",
    region: "India",
    description:
      "A social platform for the film industry to officially launch trailers and collect early audience ratings before release — live voting, trending charts, producer analytics.",
    scope: ["Mobile App", "Backend", "Producer Dashboard"],
    stack: ["React Native", "Firebase", "Video Streaming", "Node.js"],
  },
];

export const youtube = {
  channelName: "Engineer Codewala",
  handle: "@EngineerCodewala",
  subs: "18K+",
  views: "3.3M+",
  watchHours: "180K+",
  learners: "25K+",
  description:
    "Weekly tutorials on React Native, Android, Flutter, app UI, interview tips, freelancing, and corporate world experience — mostly in Hindi, built for developers who want to actually ship. I've taught React Native to 25K+ learners here.",
  topics: [
    "App development (React Native, Android, Flutter)",
    "App UI tips",
    "Interview prep",
    "Freelancing playbooks",
    "Corporate world experience",
  ],
  url: "https://www.youtube.com/@EngineerCodewala",
};

export const whyWorkWithMe = [
  { title: "End-to-end delivery", detail: "We own the full product — no handoffs between vendors." },
  { title: "Trending stack", detail: "React Native, Next.js, Node.js, and AI integrations." },
  { title: "Small, focused team", detail: "Direct communication with the developers building your product." },
  { title: "On-time delivery", detail: "Agile sprints, Jira tracking, weekly progress updates." },
  { title: "Transparent pricing", detail: "Fixed-cost or hourly — you choose, no hidden costs." },
  { title: "Post-launch support", detail: "Bug fixes, feature updates, and scaling assistance." },
];
